import {DateFormat as DateFormats, convertStringToDate} from 'src/shared/utils';

import {
    COLUMN_TYPES,
    ColumnValueTypes,
    DATA_TYPES,
    DEFAULT_DATA,
} from './constants';

import type {
    InteractiveColumn,
    OnValidate,
    UpdatedData,
    ValidateColumnValue,
} from './typedef';
import type {RowData} from '@pb/kit-component-data-table-v2';
import type {Data, Option, Store} from '@pb/kit-component-table-interactions';
import type {Locale} from '@pb/kit-context-locale';


type Props = {
    dataRow: RowData,
    columnId: string,
};

type ColumnValue = {
    columnValueType: ColumnValueTypes.STRING,
    value: string,
} | {
    columnValueType: ColumnValueTypes.DOUBLE_COLUMN_VALUE,
    value: number,
} | {
    columnValueType: ColumnValueTypes.USER_DEFINED_SELECT,
    value: string,
    color: Option['color'],
    id: number,
} | {
    columnValueType: ColumnValueTypes.DATE,
    asOfdate: string,
};

type ColumnValuesData = {
    string: string[],
    number: number[],
    select: Option[],
    selectIds: number[],
};

const getColumnValues = ({dataRow, columnId}: Props) => {
    const columnValue = dataRow.columnValues?.[columnId] as ColumnValue | undefined;

    return (columnValue ? [columnValue] : []).reduce((acc, i) => {
        switch (i.columnValueType) {
            case ColumnValueTypes.STRING:
                acc.string.push(i.value);

                break;

            case ColumnValueTypes.DOUBLE_COLUMN_VALUE:
                acc.number.push(i.value);

                break;

            case ColumnValueTypes.USER_DEFINED_SELECT:
                acc.select.push({
                    value: i.value,
                    color: i.color,
                    id: i.id,
                });

                acc.selectIds.push(i.id);

                break;

            case ColumnValueTypes.DATE:
                acc.string.push(i.asOfdate);

                break;

            default:
                break;
        }

        return acc;
    }, {string: [], number: [], select: [], selectIds: []} as ColumnValuesData);
};

type ParamsCreateNew = {
    column: InteractiveColumn,
    dataRows: RowData[],
    locale: Locale,
    relation: Map<string, Array<number>> | null,
    arrKeys: Array<string> | null,
    validator: ValidateColumnValue,
    onValidate: OnValidate,
};

const createNewData = (params: ParamsCreateNew) => {
    const {column, dataRows, locale, validator, onValidate} = params;
    const invalidData: UpdatedData[] = [];
    const data: Data[] = [];

    dataRows.forEach(dataRow => {
        if (column.columnType) {
            const {
                string,
                number,
                select,
                selectIds,
            } = getColumnValues({dataRow, columnId: column.columnId});


            switch (column.columnType) {
                case COLUMN_TYPES.STRING:
                case COLUMN_TYPES.WEBSITE:
                    const error = validator(column.columnId, string[0]);
                    if (error) {
                        invalidData.push({
                            rowId: +dataRow.id,
                            columnId: column.columnId,
                            value: string[0],
                            validationError: error.reason,
                        });
                    }
                    data.push({
                        ...DEFAULT_DATA,
                        value: string.length ? string.join(', ') : null,
                        error: error ? {type: error.reason, message: error.message || ''} : null,
                    });

                    break;


                case COLUMN_TYPES.DATE:
                    data.push({
                        ...DEFAULT_DATA,
                        value: string.length
                            ? convertStringToDate({
                                    value: string[0],
                                    dateFormat: DateFormats[locale.dateFormat as keyof typeof DateFormats],
                                    valueFormat: DateFormats.YYYYMMDD,
                                })
                            : null,
                    });
                    break;

                case COLUMN_TYPES.DOUBLE:
                    data.push({
                        ...DEFAULT_DATA,
                        value: number.length ? number[0] : null,
                    });
                    break;

                case COLUMN_TYPES.SELECT:
                    if (!select.length) {
                        data.push({...DEFAULT_DATA});

                        break;
                    }
                    data.push({...DEFAULT_DATA, value: selectIds, options: select});
                    break;


                default:
                    data.push(DEFAULT_DATA);
            }
        } else {
            data.push({selected: false, active: false});
        }
    });

    if (invalidData.length) {
        onValidate(invalidData);
    }
    return {data};
};

type ParamsCreateData = {
    columns: InteractiveColumn[],
    dataRows: RowData[],
    locale: Locale,
    validator: ValidateColumnValue,
    onValidate: OnValidate,
};

export const createDataStore = (params: ParamsCreateData) => {
    const {
        columns,
        dataRows,
        locale,
        validator,
        onValidate,
    } = params;

    const store: Store = [];

    const arrKeys = null;
    const relation = null;

    for (const column of columns) {
        const {data} = createNewData({column, dataRows, locale, relation, arrKeys, validator, onValidate});

        store.push({
            id: column.columnId,
            data,
            dataType: column.dataType || DATA_TYPES.STATIC,
            locale: {
                dateFormat: locale.dateFormat,
            },
        });
    }

    return store;
};

type ParamsUpdateData = {
    columns: InteractiveColumn[],
    dataRows: Array<RowData>,
    locale: Locale,
    store: Store,
    validator: ValidateColumnValue,
    onValidate: OnValidate,
};

export const updateDataStore = ({columns, dataRows, locale, store, validator, onValidate}: ParamsUpdateData) => {
    const _store: Store = [];

    const arrKeys = null;
    const relation = null;

    for (const column of columns) {
        const oldColumn = store.find(({id}) => column.columnId === id);

        if (oldColumn) {
            _store.push(oldColumn);
        } else {
            const {data} = createNewData({column, dataRows, locale, relation, arrKeys, validator, onValidate});

            _store.push({
                id: column.columnId,
                data,
                dataType: column.dataType || DATA_TYPES.STATIC,
                locale: {
                    dateFormat: locale.dateFormat,
                },
            });
        }
    }

    return _store;
};
